/**
 * Text typed into a session note (components/NotesModal), saved so that closing the panel,
 * reloading or losing the connection for a moment never costs the user what they wrote.
 *
 * The text used to be held by the note card's own debounce timer, which the card cleared when it
 * unmounted — so closing the panel, or reloading, within the pause after the last keystroke
 * dropped the text and left an empty note behind. Here the pending text lives outside React:
 *   - typing is shown at once and sent after a short pause, one note at a time;
 *   - closing the panel (`flushNoteTexts`) and the page being hidden or left (`pagehide`,
 *     `visibilitychange`) send whatever is still waiting immediately, with `keepalive` so the
 *     request outlives the page;
 *   - sends go through utils/saveQueue, so a dropped connection is retried with backoff and two
 *     saves of one note can never land out of order;
 *   - a refusal the server will never change its mind about (a deleted note, text it will not
 *     take) is reported once to the caller, not retried.
 */
import { apiClient } from '@/api/client';
import { hasPendingSaves, queueSave } from '@/utils/saveQueue';

/** What the server accepts for a note's text (api/models.py NoteCreate / NoteUpdate). */
export const NOTE_MAX_LENGTH = 4000;
export const NOTE_SAVE_DELAY_MS = 600;

export interface NoteSaveError {
  /** HTTP status of the refusal, or null when the request never got an answer. */
  status: number | null;
  message: string;
}

interface Pending {
  text: string;
  timer: ReturnType<typeof setTimeout>;
  onError?: (error: NoteSaveError) => void;
}

/** Notes whose latest text has not been sent yet. */
const pending = new Map<number, Pending>();

const saveKey = (id: number) => `note:${id}`;

/**
 * `text` cut to what the server accepts. JavaScript counts UTF-16 units and the server counts
 * characters, so a limit met here is never over there; a cut that would split an emoji drops the
 * half left behind, which JSON cannot carry.
 */
export function clampNoteText(text: string): string {
  if (text.length <= NOTE_MAX_LENGTH) return text;
  let end = NOTE_MAX_LENGTH;
  const last = text.charCodeAt(end - 1);
  if (last >= 0xd800 && last <= 0xdbff) end -= 1;
  return text.slice(0, end);
}

/** The status the API client put in a failed request's message ("[404] Note 3 not found"). */
function statusOf(err: unknown): number | null {
  const found = /^\[(\d{3})\]/.exec(err instanceof Error ? err.message : '');
  return found ? Number(found[1]) : null;
}

function send(id: number, keepalive: boolean): void {
  const entry = pending.get(id);
  if (!entry) return;
  clearTimeout(entry.timer);
  pending.delete(id);
  const text = clampNoteText(entry.text);
  queueSave(saveKey(id), async () => {
    try {
      await apiClient.updateNote(id, { text }, keepalive ? { keepalive: true } : undefined);
    } catch (err) {
      const status = statusOf(err);
      // 4xx: the server has answered and will answer the same again — retrying only delays the
      // message. Anything else (no connection, a deploy) is left to the queue to retry.
      if (status !== null && status >= 400 && status < 500 && (err as Error).name !== 'ViewOnlyError') {
        entry.onError?.({ status, message: (err as Error).message });
        return;
      }
      throw err;
    }
  });
}

/** Set a note's text: kept here at once, sent after a short pause (replacing any earlier text still waiting). */
export function scheduleNoteText(id: number, text: string, onError?: (error: NoteSaveError) => void): void {
  installLeaveHandlers();
  const earlier = pending.get(id);
  if (earlier) clearTimeout(earlier.timer);
  pending.set(id, { text, onError, timer: setTimeout(() => send(id, false), NOTE_SAVE_DELAY_MS) });
}

/** Send every note's waiting text now — the panel closing, or the page about to go. */
export function flushNoteTexts(opts: { keepalive?: boolean } = {}): void {
  for (const id of [...pending.keys()]) send(id, !!opts.keepalive);
}

/** Whether a note has text the server does not hold yet (waiting, or sent and not yet answered). */
export function hasUnsavedNoteText(id: number): boolean {
  return pending.has(id) || hasPendingSaves(saveKey(id));
}

/** A note was deleted: text still waiting for it would only come back as a 404. */
export function dropNoteText(id: number): void {
  const entry = pending.get(id);
  if (entry) clearTimeout(entry.timer);
  pending.delete(id);
}

let leaveHandlersInstalled = false;

/**
 * Send what is waiting when the tab is hidden or closed. A phone switching apps or a reload fires
 * `pagehide` and/or a `visibilitychange` to hidden; either may be the only one, so both flush.
 */
export function installLeaveHandlers(): void {
  if (leaveHandlersInstalled || typeof window === 'undefined' || typeof document === 'undefined') return;
  leaveHandlersInstalled = true;
  window.addEventListener('pagehide', () => flushNoteTexts({ keepalive: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushNoteTexts({ keepalive: true });
  });
}

/** Test seam: let the next `scheduleNoteText` install the page-leave handlers again. */
export function _forgetLeaveHandlers(): void { leaveHandlersInstalled = false; }
